import React, { useState } from 'react';
import './PilotEnrollmentModal.css';
import { sendPilotEnrollmentEmail, sendEmailViaMailto } from '../services/emailService';

interface PilotEnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PilotEnrollmentModal: React.FC<PilotEnrollmentModalProps> = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // College Information
    collegeName: '',
    collegeType: '',
    location: '',
    establishedYear: '',
    
    // Contact Person Details
    contactPersonName: '',
    designation: '',
    department: '',
    email: '',
    phone: '',
    countryCode: '+91',
    
    // Program Details
    targetStudents: '',
    expectedBatchSize: '',
    preferredSemester: '',
    deliveryMode: '',
    preferredSchedule: '',
    
    // Additional Information
    currentAIPrograms: '',
    specificRequirements: '',
    budgetRange: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Try to send email using EmailJS first
      const emailSent = await sendPilotEnrollmentEmail(formData);
      
      if (emailSent) {
        alert('Thank you for your interest in our AI Pilot Program! Your request has been submitted successfully. Our team will contact you within 24 hours to discuss the next steps.');
      } else {
        // Fallback to mailto if EmailJS fails
        sendEmailViaMailto(formData);
        alert('Thank you for your interest! Your email client will open to send the request. Please send the email to complete your submission.');
      }
      
      // Reset form and close modal
      setFormData({
        collegeName: '',
        collegeType: '',
        location: '',
        establishedYear: '',
        contactPersonName: '',
        designation: '',
        department: '',
        email: '',
        phone: '',
        countryCode: '+91',
        targetStudents: '',
        expectedBatchSize: '',
        preferredSemester: '',
        deliveryMode: '',
        preferredSchedule: '',
        currentAIPrograms: '',
        specificRequirements: '',
        budgetRange: ''
      });
      
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your request. Please try again or contact us directly at globalsansys@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pilot-modal-overlay" onClick={onClose}>
      <div className="pilot-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="pilot-modal-close" onClick={onClose}>×</button>
        
        <div className="pilot-modal-header">
          <h2>Start AI Pilot Program for Your College</h2>
          <p>Share your college details and requirements. We'll create a customized pilot plan for your students.</p>
        </div>

        <form onSubmit={handleSubmit} className="pilot-enrollment-form">
          {/* College Information Section */}
          <div className="form-section">
            <h3>College Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>College Name*</label>
                <input
                  type="text"
                  name="collegeName"
                  placeholder="Enter college name"
                  value={formData.collegeName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>College Type*</label>
                <select 
                  name="collegeType" 
                  value={formData.collegeType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select college type</option>
                  <option value="Engineering College">Engineering College</option>
                  <option value="University">University</option>
                  <option value="Autonomous College">Autonomous College</option>
                  <option value="Private College">Private College</option>
                  <option value="Government College">Government College</option>
                </select>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Location (City, State)*</label>
                <input
                  type="text"
                  name="location"
                  placeholder="e.g., Bangalore, Karnataka"
                  value={formData.location}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Established Year</label>
                <input
                  type="number"
                  name="establishedYear"
                  placeholder="e.g., 1995"
                  value={formData.establishedYear}
                  onChange={handleInputChange}
                  min="1800"
                  max="2024"
                />
              </div>
            </div>
          </div>

          {/* Contact Person Details Section */}
          <div className="form-section">
            <h3>Contact Person Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Full Name*</label>
                <input
                  type="text"
                  name="contactPersonName"
                  placeholder="Enter your full name"
                  value={formData.contactPersonName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Designation*</label>
                <select 
                  name="designation" 
                  value={formData.designation}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select designation</option>
                  <option value="Principal">Principal</option>
                  <option value="Vice Principal">Vice Principal</option>
                  <option value="HOD">Head of Department</option>
                  <option value="Professor">Professor</option>
                  <option value="Associate Professor">Associate Professor</option>
                  <option value="Assistant Professor">Assistant Professor</option>
                  <option value="Training & Placement Officer">Training & Placement Officer</option>
                  <option value="Academic Coordinator">Academic Coordinator</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Department*</label>
                <select 
                  name="department" 
                  value={formData.department}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select department</option>
                  <option value="Computer Science & Engineering">Computer Science & Engineering</option>
                  <option value="Information Technology">Information Technology</option>
                  <option value="Electronics & Communication">Electronics & Communication</option>
                  <option value="Mechanical Engineering">Mechanical Engineering</option>
                  <option value="Civil Engineering">Civil Engineering</option>
                  <option value="MCA">Master of Computer Applications</option>
                  <option value="Training & Placement">Training & Placement</option>
                  <option value="Administration">Administration</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Email Address*</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group phone-group">
                <label>Phone Number*</label>
                <div className="phone-input">
                  <select 
                    name="countryCode" 
                    value={formData.countryCode}
                    onChange={handleInputChange}
                    className="country-code"
                  >
                    <option value="+91">+91</option>
                    <option value="+1">+1</option>
                    <option value="+44">+44</option>
                    <option value="+61">+61</option>
                  </select>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Enter phone number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Program Requirements Section */}
          <div className="form-section">
            <h3>Program Requirements</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Target Students*</label>
                <select 
                  name="targetStudents" 
                  value={formData.targetStudents}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select target students</option>
                  <option value="Engineering - All Branches">Engineering - All Branches</option>
                  <option value="Engineering - CSE/IT Only">Engineering - CSE/IT Only</option>
                  <option value="MCA Students">MCA Students</option>
                  <option value="Mixed (Engineering + MCA)">Mixed (Engineering + MCA)</option>
                  <option value="Final Year Students Only">Final Year Students Only</option>
                </select>
              </div>
              <div className="form-group">
                <label>Expected Batch Size*</label>
                <select 
                  name="expectedBatchSize" 
                  value={formData.expectedBatchSize}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select batch size</option>
                  <option value="20-30 students">20-30 students</option>
                  <option value="30-50 students">30-50 students</option>
                  <option value="50-80 students">50-80 students</option>
                  <option value="80-100 students">80-100 students</option>
                  <option value="100+ students">100+ students</option>
                </select>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Preferred Semester/Time*</label>
                <select 
                  name="preferredSemester" 
                  value={formData.preferredSemester}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select preferred time</option>
                  <option value="Current Semester">Current Semester</option>
                  <option value="Next Semester">Next Semester</option>
                  <option value="Summer Break">Summer Break</option>
                  <option value="Winter Break">Winter Break</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>
              <div className="form-group">
                <label>Preferred Delivery Mode*</label>
                <select 
                  name="deliveryMode" 
                  value={formData.deliveryMode}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select delivery mode</option>
                  <option value="Hybrid (Recommended)">Hybrid (Recommended)</option>
                  <option value="Online">Online</option>
                  <option value="Offline">Offline</option>
                </select>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Preferred Schedule*</label>
                <select 
                  name="preferredSchedule" 
                  value={formData.preferredSchedule}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select schedule</option>
                  <option value="Weekends">Weekends</option>
                  <option value="Weekday Evenings">Weekday Evenings</option>
                  <option value="Mixed (Weekends + Evenings)">Mixed (Weekends + Evenings)</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>
              <div className="form-group">
                <label>Budget Range (Optional)</label>
                <select 
                  name="budgetRange" 
                  value={formData.budgetRange}
                  onChange={handleInputChange}
                >
                  <option value="">Select budget range</option>
                  <option value="Under ₹50,000">Under ₹50,000</option>
                  <option value="₹50,000 - ₹1,00,000">₹50,000 - ₹1,00,000</option>
                  <option value="₹1,00,000 - ₹2,00,000">₹1,00,000 - ₹2,00,000</option>
                  <option value="₹2,00,000 - ₹5,00,000">₹2,00,000 - ₹5,00,000</option>
                  <option value="Above ₹5,00,000">Above ₹5,00,000</option>
                  <option value="Need Discussion">Need Discussion</option>
                </select>
              </div>
            </div>
          </div>

          {/* Additional Information Section */}
          <div className="form-section">
            <h3>Additional Information</h3>
            <div className="form-group">
              <label>Current AI/ML Programs (if any)</label>
              <textarea
                name="currentAIPrograms"
                placeholder="Describe any existing AI/ML courses, workshops, or programs at your college"
                value={formData.currentAIPrograms}
                onChange={handleInputChange}
                rows={3}
              />
            </div>
            
            <div className="form-group">
              <label>Specific Requirements or Expectations</label>
              <textarea
                name="specificRequirements"
                placeholder="Any specific requirements, expectations, or questions about the pilot program"
                value={formData.specificRequirements}
                onChange={handleInputChange}
                rows={3}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Pilot Program Request'}
            </button>
            <p className="form-note">
              Our team will review your request and contact you within 24 hours to discuss the customized pilot plan.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PilotEnrollmentModal;